import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { handleError } from './utils';

// TODO: add backend response for each request to show as notification

// Generic create hook using react-query mutation
export const useCreate = (request, options = {}) => {
  const { onSuccess, onError } = options;
  const [error, setError] = useState({});

  const mutation = useMutation((payload) => request(payload), {
    onSuccess: (data, variables, context) => {
      onSuccess && onSuccess(data, variables, context);
    },
    onError: (err) => {
      handleError(
        err?.response?.status,
        err?.response?.statusText,
        err?.response?.data?.message,
        setError
      );
      onError && onError(err);
    }
  });

  const create = (payload) => {
    mutation.mutate(payload);
  };

  return { isLoading: mutation.isLoading, error, create };
};

// If requested specific range, use - key: ['orders', selectedDate]
export const useFetch = (request, options = {}, params) => {
  const {
    key: externalKey,
    enabled: externalEnabled
  } = options;
  const enabled = typeof externalEnabled === 'boolean' ? externalEnabled : true;
  const queryKey = externalKey || [request.name || 'fetch', params];
  const [error, setError] = useState({});

  const queryFn = async () => {
    const res = await request(params);
    return res;
  };

  const {
    data = null,
    isLoading,
    refetch
  } = useQuery({
    queryKey,
    queryFn,
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onError: (err) => {
      handleError( // Check statuses to logout first, than displad in ErrorAlert.jsx
        err?.response?.status,
        err?.response?.statusText,
        err?.response?.data?.message,
        setError
      );
    }
  });

  const reloadData = () => refetch();

  return { data, isLoading, error, reloadData };
};

export const useEdit = (request, options = {}) => {
  const { onSuccess } = options;
  const [error, setError] = useState({});

  const mutation = useMutation((payload) => request(payload), {
    onSuccess: (data, variables, context) => {
      onSuccess && onSuccess(data, variables, context);
    },
    onError: (err) => {
      handleError(
        err?.response?.status,
        err?.response?.statusText,
        err?.response?.data?.message,
        setError
      );
    }
  });

  const sendEditDataRequest = (payload) => {
    mutation.mutate(payload);
  };

  return { isLoading: mutation.isLoading, error, sendEditDataRequest };
};

export const useRemove = (request, options = {}) => {
  const { callback } = options;
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: request,

    onSuccess: () => {
      callback?.();
    },

    onError: (err) => {
      handleError(
        err?.response?.status,
        err?.response?.statusText,
        err?.response?.data?.message,
        setError
      );
    },
  });

  const sendRemoveRequest = () => {
    mutation.mutate();
  };

  return {
    sendRemoveRequest,
    sendRemoveRequestAsync: mutation.mutateAsync, // For removing one by one
    isLoading: mutation.isPending, // ✅ v5 correct
    error,
  };
};
